import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { pagosAPI } from '../../services/api';

const estadoConfig = {
  pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
  confirmado: { label: 'Confirmado', color: 'bg-green-100 text-green-800', icon: '✅' },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-800', icon: '❌' },
};

const conceptoLabels = {
  mensualidad: 'Mensualidad',
  uniforme: 'Uniforme',
  torneo: 'Torneo',
  otro: 'Otro',
};

const metodoLabels = {
  pago_movil: 'Pago Móvil',
  transferencia: 'Transferencia',
  efectivo_bs: 'Efectivo Bs',
  efectivo_usd: 'Efectivo USD',
  zelle: 'Zelle',
};

const meses = [
  '', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const PagosList = () => {
  const [searchParams] = useSearchParams();
  const [activeView, setActiveView] = useState(searchParams.get('vista') || 'pagos');
  const [pagos, setPagos] = useState([]);
  const [tasas, setTasas] = useState([]);
  const [tasaActual, setTasaActual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTasas, setLoadingTasas] = useState(true);
  const [error, setError] = useState('');
  const [filtroEstado, setFiltroEstado] = useState(searchParams.get('estado') || 'pendiente');
  const [confirmando, setConfirmando] = useState(null);
  const [rechazando, setRechazando] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadPagos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEstado]);

  useEffect(() => {
    loadTasas();
  }, []);

  const loadPagos = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { limit: 100 };
      if (filtroEstado) params.estado = filtroEstado;
      const response = await pagosAPI.getAll(params);
      setPagos(response.data);
    } catch (err) {
      setError('Error cargando pagos');
    } finally {
      setLoading(false);
    }
  };

  const loadTasas = async () => {
    try {
      setLoadingTasas(true);
      const [historialRes, actualRes] = await Promise.all([
        pagosAPI.getHistorialTasas(60),
        pagosAPI.getTasaBCV()
      ]);
      setTasas(historialRes.data);
      setTasaActual(actualRes.data);
    } catch (err) {
      console.error('Error cargando tasas:', err);
    } finally {
      setLoadingTasas(false);
    }
  };

  const handleConfirmar = async (id) => {
    try {
      setActionLoading(true);
      await pagosAPI.confirmar(id);
      setConfirmando(null);
      loadPagos();
    } catch (err) {
      setError('Error al confirmar el pago');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRechazar = async (id) => {
    if (!motivoRechazo.trim()) {
      setError('Debe indicar el motivo del rechazo');
      return;
    }
    try {
      setActionLoading(true);
      await pagosAPI.rechazar(id, motivoRechazo);
      setRechazando(null);
      setMotivoRechazo('');
      loadPagos();
    } catch (err) {
      setError('Error al rechazar el pago');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('es-VE');
  };

  const formatMonto = (monto) => {
    if (!monto) return '0.00';
    return parseFloat(monto).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Build a lookup map of fecha -> tasa from the tasas array
  const tasasMap = {};
  tasas.forEach(t => {
    tasasMap[t.fecha?.split('T')[0]] = parseFloat(t.tasa);
  });

  // Verify payment: compare tasa_bcv used in payment vs actual BCV rate that day
  const verificarPago = (pago) => {
    if (!pago.tasa_bcv || !pago.monto_bs || !pago.monto_usd) return null;
    const fechaPago = pago.fecha_pago?.split('T')[0];
    const tasaDia = tasasMap[fechaPago];
    const tasaUsada = parseFloat(pago.tasa_bcv);

    if (!tasaDia) return { status: 'sin_tasa', message: 'Sin tasa BCV para esa fecha' };

    const diff = Math.abs(tasaUsada - tasaDia);
    const pctDiff = (diff / tasaDia) * 100;

    // Also check if the USD amount matches: monto_bs / tasa_bcv should ≈ monto_usd
    const usdEsperado = parseFloat(pago.monto_bs) / tasaDia;
    const usdRegistrado = parseFloat(pago.monto_usd);
    const diffUsd = Math.abs(usdEsperado - usdRegistrado);
    const pctDiffUsd = usdRegistrado > 0 ? (diffUsd / usdRegistrado) * 100 : 0;

    if (pctDiff > 5 || pctDiffUsd > 5) {
      return {
        status: 'alerta',
        message: `Tasa usada: ${tasaUsada.toFixed(2)} vs BCV del día: ${tasaDia.toFixed(2)} (${pctDiff.toFixed(1)}% dif). USD esperado: $${usdEsperado.toFixed(2)}`,
        tasaDia,
        usdEsperado: usdEsperado.toFixed(2)
      };
    }

    return { status: 'ok', message: 'Verificado ✓', tasaDia };
  };

  const tabs = [
    { key: 'pendiente', label: 'Pendientes', icon: '⏳' },
    { key: 'confirmado', label: 'Confirmados', icon: '✅' },
    { key: 'rechazado', label: 'Rechazados', icon: '❌' },
    { key: '', label: 'Todos', icon: '📋' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Pagos</h1>
          <p className="text-gray-500 text-sm mt-1">Revisa, confirma o rechaza los pagos registrados</p>
        </div>
        <Link to="/admin" className="text-lsfc-blue-600 hover:text-lsfc-blue-800 font-medium text-sm">
          ← Volver al Dashboard
        </Link>
      </div>

      {/* View toggle: Pagos | Tasa BCV */}
      <div className="flex gap-2 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        <button
          onClick={() => setActiveView('pagos')}
          className={`px-5 py-2 rounded-lg font-medium text-sm transition-all ${
            activeView === 'pagos' ? 'bg-white text-lsfc-blue-700 shadow' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          💰 Pagos
        </button>
        <button
          onClick={() => setActiveView('tasas')}
          className={`px-5 py-2 rounded-lg font-medium text-sm transition-all ${
            activeView === 'tasas' ? 'bg-white text-lsfc-blue-700 shadow' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          📈 Tasa BCV
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
          <button onClick={() => setError('')} className="ml-2 font-bold">✕</button>
        </div>
      )}

      {/* =================== TASA BCV VIEW =================== */}
      {activeView === 'tasas' && (
        <div className="space-y-6">
          {/* Current rate card */}
          {tasaActual && (
            <div className="bg-gradient-to-r from-lsfc-blue-600 to-lsfc-blue-800 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-200 text-sm">Tasa BCV Hoy</p>
                  <p className="text-4xl font-bold mt-1">{tasaActual.tasa?.toFixed(4)} Bs/$</p>
                  <p className="text-blue-200 text-sm mt-2">{tasaActual.fecha}</p>
                </div>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-3xl">💱</span>
                </div>
              </div>
            </div>
          )}

          {/* History table */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
              <h2 className="font-bold text-gray-800">Historial de Tasas BCV</h2>
              <span className="text-sm text-gray-500">{tasas.length} registros</span>
            </div>

            {loadingTasas ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-lsfc-blue-600"></div>
              </div>
            ) : tasas.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-4xl">📊</span>
                <p className="text-gray-500 mt-3">No hay tasas registradas</p>
                <p className="text-gray-400 text-sm mt-1">Las tasas se guardan automáticamente cada vez que se registra un pago</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Día</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Tasa (Bs/$)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Variación</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tasas.map((t, idx) => {
                      const tasa = parseFloat(t.tasa);
                      const tasaAnterior = idx < tasas.length - 1 ? parseFloat(tasas[idx + 1].tasa) : null;
                      const variacion = tasaAnterior ? tasa - tasaAnterior : null;
                      const pctVariacion = tasaAnterior ? ((variacion / tasaAnterior) * 100) : null;
                      const fecha = new Date(t.fecha);
                      const diasSemana = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

                      return (
                        <tr key={t.fecha} className={`hover:bg-gray-50 ${idx === 0 ? 'bg-blue-50' : ''}`}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-800">
                            {fecha.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {diasSemana[fecha.getUTCDay()]}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-bold text-sm ${idx === 0 ? 'text-lsfc-blue-700 text-base' : 'text-gray-800'}`}>
                              {tasa.toFixed(4)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {variacion !== null ? (
                              <span className={`text-sm font-medium ${
                                variacion > 0 ? 'text-red-600' : variacion < 0 ? 'text-green-600' : 'text-gray-400'
                              }`}>
                                {variacion > 0 ? '▲' : variacion < 0 ? '▼' : '–'}{' '}
                                {Math.abs(variacion).toFixed(4)}
                                <span className="text-xs ml-1">
                                  ({pctVariacion > 0 ? '+' : ''}{pctVariacion.toFixed(2)}%)
                                </span>
                              </span>
                            ) : (
                              <span className="text-gray-300 text-sm">--</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =================== PAGOS VIEW =================== */}
      {activeView === 'pagos' && (
        <>
          {/* Filtro por estado */}
          <div className="flex flex-wrap gap-2 mb-6">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFiltroEstado(tab.key)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  filtroEstado === tab.key
                    ? 'bg-lsfc-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Confirm modal */}
          {confirmando && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
                <h3 className="text-lg font-bold text-gray-800 mb-2">¿Confirmar este pago?</h3>
                <p className="text-sm text-gray-500 mb-1">
                  <strong>{confirmando.jugador_nombres} {confirmando.jugador_apellidos}</strong>
                </p>
                <p className="text-sm text-gray-500 mb-1">
                  {conceptoLabels[confirmando.concepto] || confirmando.concepto} • {metodoLabels[confirmando.metodo_pago] || confirmando.metodo_pago}
                </p>
                <p className="text-lg font-bold text-green-600 mb-2">
                  ${formatMonto(confirmando.monto_usd)} USD / Bs. {formatMonto(confirmando.monto_bs)}
                </p>

                {/* Verification in confirm modal */}
                {(() => {
                  const v = verificarPago(confirmando);
                  if (v && v.status === 'alerta') {
                    return (
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg mb-3 text-xs text-orange-700">
                        ⚠️ <strong>Alerta:</strong> {v.message}
                      </div>
                    );
                  }
                  if (v && v.status === 'ok') {
                    return (
                      <div className="p-2 bg-green-50 border border-green-200 rounded-lg mb-3 text-xs text-green-700">
                        ✅ Tasa verificada con BCV del día ({v.tasaDia?.toFixed(2)} Bs/$)
                      </div>
                    );
                  }
                  return null;
                })()}

                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmando(null)}
                    disabled={actionLoading}
                    className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleConfirmar(confirmando.id)}
                    disabled={actionLoading}
                    className="flex-1 py-2 bg-green-600 text-white rounded-lg font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : '✅'} Confirmar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Reject modal */}
          {rechazando && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full">
                <h3 className="text-lg font-bold text-gray-800 mb-2">Rechazar pago</h3>
                <p className="text-sm text-gray-500 mb-1">
                  <strong>{rechazando.jugador_nombres} {rechazando.jugador_apellidos}</strong>
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  {conceptoLabels[rechazando.concepto] || rechazando.concepto} • ${formatMonto(rechazando.monto_usd)}
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Motivo del rechazo *</label>
                  <textarea
                    value={motivoRechazo}
                    onChange={(e) => setMotivoRechazo(e.target.value)}
                    placeholder="Ej: Comprobante no coincide con el monto, referencia duplicada..."
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none h-24"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setRechazando(null); setMotivoRechazo(''); }}
                    disabled={actionLoading}
                    className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleRechazar(rechazando.id)}
                    disabled={actionLoading || !motivoRechazo.trim()}
                    className="flex-1 py-2 bg-red-600 text-white rounded-lg font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : '❌'} Rechazar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lsfc-blue-600"></div>
            </div>
          ) : pagos.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <div className="text-5xl mb-4">📭</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay pagos</h3>
              <p className="text-gray-500">
                {filtroEstado === 'pendiente'
                  ? 'No hay pagos pendientes por confirmar'
                  : filtroEstado === 'confirmado'
                  ? 'No hay pagos confirmados'
                  : filtroEstado === 'rechazado'
                  ? 'No hay pagos rechazados'
                  : 'No se encontraron pagos'}
              </p>
            </div>
          ) : (
            <>
              {/* Count + current rate */}
              <div className="bg-white rounded-t-xl shadow-lg p-4 border-b border-gray-200 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {pagos.length} pago{pagos.length !== 1 ? 's' : ''} encontrado{pagos.length !== 1 ? 's' : ''}
                </p>
                {tasaActual && (
                  <p className="text-sm text-gray-500">
                    Tasa BCV hoy: <span className="font-bold text-lsfc-blue-600">{tasaActual.tasa?.toFixed(2)} Bs/$</span>
                  </p>
                )}
              </div>

              {/* Desktop table */}
              <div className="hidden lg:block bg-white rounded-b-xl shadow-lg overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Jugador</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Representante</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Concepto</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Método</th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Monto</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Tasa</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Ref</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fecha</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Check</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {pagos.map((pago) => {
                      const estado = estadoConfig[pago.estado] || estadoConfig.pendiente;
                      const verificacion = verificarPago(pago);
                      return (
                        <tr key={pago.id} className={`hover:bg-gray-50 transition-colors ${
                          verificacion?.status === 'alerta' ? 'bg-orange-50/50' : ''
                        }`}>
                          <td className="px-3 py-3">
                            <p className="font-medium text-gray-800 text-sm">
                              {pago.jugador_nombres} {pago.jugador_apellidos}
                            </p>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-600">
                            {pago.representante_nombre} {pago.representante_apellido}
                          </td>
                          <td className="px-3 py-3">
                            <span className="text-sm">{conceptoLabels[pago.concepto] || pago.concepto}</span>
                            {pago.concepto === 'mensualidad' && pago.mes_correspondiente && (
                              <p className="text-xs text-gray-400">{meses[pago.mes_correspondiente]} {pago.ano_correspondiente}</p>
                            )}
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-600">
                            {metodoLabels[pago.metodo_pago] || pago.metodo_pago}
                          </td>
                          <td className="px-3 py-3 text-right">
                            <p className="font-bold text-green-600 text-sm">${formatMonto(pago.monto_usd)}</p>
                            <p className="text-xs text-gray-400">Bs. {formatMonto(pago.monto_bs)}</p>
                          </td>
                          <td className="px-3 py-3 text-center">
                            {pago.tasa_bcv ? (
                              <span className="text-xs text-gray-500">{parseFloat(pago.tasa_bcv).toFixed(2)}</span>
                            ) : (
                              <span className="text-xs text-gray-300">--</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-xs text-gray-600">
                            {pago.referencia || <span className="text-gray-300">--</span>}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-600">
                            {formatDate(pago.fecha_pago)}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${estado.color}`}>
                              {estado.icon} {estado.label}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            {verificacion ? (
                              verificacion.status === 'ok' ? (
                                <span className="text-green-500 text-sm" title={verificacion.message}>✅</span>
                              ) : verificacion.status === 'alerta' ? (
                                <span
                                  className="text-orange-500 text-sm cursor-help"
                                  title={verificacion.message}
                                >
                                  ⚠️
                                </span>
                              ) : (
                                <span className="text-gray-300 text-sm" title={verificacion.message}>❓</span>
                              )
                            ) : (
                              <span className="text-gray-200">--</span>
                            )}
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <Link
                                to={`/admin/pagos/${pago.id}`}
                                className="px-2 py-1 bg-lsfc-blue-50 text-lsfc-blue-700 rounded text-xs font-medium hover:bg-lsfc-blue-100"
                              >
                                Ver
                              </Link>
                              {pago.estado === 'pendiente' && (
                                <>
                                  <button
                                    onClick={() => setConfirmando(pago)}
                                    className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-medium hover:bg-green-100"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={() => setRechazando(pago)}
                                    className="px-2 py-1 bg-red-50 text-red-700 rounded text-xs font-medium hover:bg-red-100"
                                  >
                                    ✕
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="lg:hidden space-y-3 mt-3">
                {pagos.map((pago) => {
                  const estado = estadoConfig[pago.estado] || estadoConfig.pendiente;
                  const verificacion = verificarPago(pago);
                  return (
                    <div key={pago.id} className={`bg-white rounded-xl shadow-md p-4 ${
                      verificacion?.status === 'alerta' ? 'border-l-4 border-orange-400' : ''
                    }`}>
                      {/* Top row */}
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-gray-800 text-sm truncate">
                            {pago.jugador_nombres} {pago.jugador_apellidos}
                          </p>
                          <p className="text-xs text-gray-400">
                            Rep: {pago.representante_nombre} {pago.representante_apellido}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          {verificacion?.status === 'alerta' && (
                            <span className="text-orange-500 text-xs" title={verificacion.message}>⚠️</span>
                          )}
                          {verificacion?.status === 'ok' && (
                            <span className="text-green-500 text-xs">✅</span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${estado.color}`}>
                            {estado.icon} {estado.label}
                          </span>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="flex justify-between items-center text-xs text-gray-500 mb-2">
                        <span>
                          {conceptoLabels[pago.concepto] || pago.concepto}
                          {pago.concepto === 'mensualidad' && pago.mes_correspondiente && (
                            <> • {meses[pago.mes_correspondiente]} {pago.ano_correspondiente}</>
                          )}
                        </span>
                        <span>{metodoLabels[pago.metodo_pago] || pago.metodo_pago}</span>
                      </div>

                      {/* Amount and date */}
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <span className="font-bold text-green-600">${formatMonto(pago.monto_usd)}</span>
                          <span className="text-xs text-gray-400 ml-2">Bs. {formatMonto(pago.monto_bs)}</span>
                        </div>
                        <span className="text-xs text-gray-400">{formatDate(pago.fecha_pago)}</span>
                      </div>

                      {/* Tasa and ref */}
                      <div className="flex justify-between items-center text-xs text-gray-400 mb-2">
                        {pago.tasa_bcv && (
                          <span>Tasa: {parseFloat(pago.tasa_bcv).toFixed(2)}</span>
                        )}
                        {pago.referencia && (
                          <span>Ref: {pago.referencia}</span>
                        )}
                      </div>

                      {/* Alert banner */}
                      {verificacion?.status === 'alerta' && (
                        <div className="p-2 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-700 mb-2">
                          ⚠️ {verificacion.message}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 pt-2 border-t border-gray-100">
                        <Link
                          to={`/admin/pagos/${pago.id}`}
                          className="flex-1 py-2 bg-lsfc-blue-50 text-lsfc-blue-700 rounded-lg text-xs font-medium text-center hover:bg-lsfc-blue-100"
                        >
                          Ver Detalle
                        </Link>
                        {pago.estado === 'pendiente' && (
                          <>
                            <button
                              onClick={() => setConfirmando(pago)}
                              className="flex-1 py-2 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100"
                            >
                              ✅ Confirmar
                            </button>
                            <button
                              onClick={() => setRechazando(pago)}
                              className="flex-1 py-2 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100"
                            >
                              ❌ Rechazar
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PagosList;
